import React from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import Paper from '@material-ui/core/Paper';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { scroller } from 'react-scroll';
import isEmpty from 'lodash/isEmpty';
import * as actionCreators from 'actions/conversations';
import { clearPreviousConversation } from 'actions/conversation';
import { list as tagsList } from 'modules/tags/redux/selectors';
import { fetchTagsRequest } from 'modules/tags/redux/actionCreators';
import { userData, isImpersonating as isImpersonatingSelector } from 'selectors/user';
import Main from 'components/templates/Main';
import ConversationsList from '../components/List';
import ConversationsFilters from '../components/ListFilters';

// Let the list paint before scrolling, then animate.
const SCROLL_RESTORE_DELAY_MS = 500;
const SCROLL_DURATION_MS = 800;

// Fixed chrome above the scrollable list. Mirrors Main.js: content marginTop is
// 56 on xs and 64 from sm up, and impersonation stacks a banner above that.
const HEADER_HEIGHT_MOBILE = 56;
const HEADER_HEIGHT_DESKTOP = 64;
const IMPERSONATION_BANNER_HEIGHT = 48;

// The restored row can be several pages down. Hunt for it, but give up rather
// than walking the whole list on a large account -- each page is a round trip
// with the loader on screen and a growing DOM. Page size is the conversations
// reducer's `limit`, so the row budget follows from there.
const MAX_RESTORE_PAGE_FETCHES = 3;

// Negative offset so the restored row lands just below the fixed chrome instead
// of behind it. Exported for tests -- pure, no theme or DOM access.
export const scrollRestoreOffset = ({ isDesktop, isImpersonating }) =>
  -(
    (isDesktop ? HEADER_HEIGHT_DESKTOP : HEADER_HEIGHT_MOBILE) +
    (isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0)
  );

const List = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { isFetching, data, filter, offset, totalRecords } = useSelector(
    (state) => state.conversations,
  );
  const currentUser = useSelector(userData);
  const tags = useSelector(tagsList);
  const isImpersonating = useSelector(isImpersonatingSelector);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  // `state.conversation.id` is only ever set, never reset -- clearConversation
  // leaves it deliberately, since that runs on conversation-view unmount, which
  // is exactly when this list still needs it. Read it once via the store rather
  // than useSelector: this is a mount-time snapshot, and subscribing would only
  // re-render the list when the mount effect below clears the pointer.
  // Reading the store during render is the pattern React 18 concurrent mode can
  // tear on; safe here on 17, and it is a single mount-time snapshot rather than
  // a subscription. Revisit with useSyncExternalStore on an 18 upgrade.
  const justViewedIdRef = React.useRef(store.getState().conversation.id);
  const justViewedId = justViewedIdRef.current;
  const hasRestoredScroll = React.useRef(false);
  const restorePageFetches = React.useRef(0);
  const hasMore = totalRecords > offset;

  const fetchMoreData = () => {
    // Read isFetching live rather than from the render closure. The restore hunt
    // below calls this directly, bypassing InfiniteScroll's own latch, so a
    // scroll event in the same tick would otherwise dispatch a second request at
    // the same offset -- and the reducer appends with $push, so the page would
    // land twice (duplicate keys, offset jumping by two pages). The mount effect
    // below keeps reading the closure: it runs once, at first render, when the
    // two agree.
    if (!store.getState().conversations.isFetching) {
      dispatch(actionCreators.fetchConversationsRequest());
    }
  };

  React.useEffect(() => {
    if (!isFetching) {
      dispatch(actionCreators.fetchConversationsRequest());
    }

    dispatch(fetchTagsRequest());

    // Consume the pointer so the restore fires once, on the visit you return
    // on, rather than on every later visit to the list.
    if (justViewedId) {
      dispatch(clearPreviousConversation());
    }

    return () => {
      dispatch(actionCreators.clearConversations());
    };
    // Mount only: this is the initial load and the one-shot pointer read.
  }, []);

  React.useEffect(() => {
    if (hasRestoredScroll.current || !justViewedId || isFetching || isEmpty(data)) {
      return undefined;
    }

    if (!data.find((c) => c.id === justViewedId)) {
      // Out of pages, or hunted long enough: stop. Without this the effect
      // re-fires on every response -- an empty $push still hands back a new
      // `data` identity -- and pages to the end of the list and beyond.
      if (!hasMore || restorePageFetches.current >= MAX_RESTORE_PAGE_FETCHES) {
        hasRestoredScroll.current = true;
        return undefined;
      }

      restorePageFetches.current += 1;
      fetchMoreData();
      return undefined;
    }

    const timer = setTimeout(() => {
      // Belt and braces. The cleanup below already clears this timer before
      // any other path can latch, but that holds only because React flushes
      // the filter dispatch well inside the delay. Re-check so an abandoned
      // restore can never scroll anyway.
      if (hasRestoredScroll.current) {
        return;
      }

      // Latch inside the callback, not before it: a `data` change within the
      // delay cancels this timer, and the retry should still scroll. Latching
      // here means paging after the restore can't yank the user back.
      hasRestoredScroll.current = true;

      scroller.scrollTo('previousConversationId', {
        duration: SCROLL_DURATION_MS,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: scrollRestoreOffset({ isDesktop, isImpersonating }),
      });
    }, SCROLL_RESTORE_DELAY_MS);

    return () => clearTimeout(timer);
    // fetchMoreData is deliberately omitted -- it is rebuilt every render and
    // only closes over isFetching, which is already a dependency.
  }, [data, isFetching, isDesktop, isImpersonating, justViewedId, hasMore]);

  const changeFilter = (event) => {
    const { value } = event.target;

    // Abandon a pending restore: the filter reset empties `data`, and scrolling
    // to the old row once the new result set lands would yank the user.
    hasRestoredScroll.current = true;

    if (value.some((v) => !v)) {
      dispatch(actionCreators.setConversationsFilter({ filter: [] }));
    } else {
      dispatch(actionCreators.setConversationsFilter({ filter: value }));
    }

    return fetchMoreData();
  };

  const isLoading = isFetching || isEmpty(currentUser);

  return (
    <Main title={<FormattedMessage id="conversations.Messages" defaultMessage="Messages" />}>
      <Paper>
        <ConversationsFilters changeFilter={changeFilter} filter={filter} tags={tags} />
        <ConversationsList
          hasMore={hasMore}
          fetchMoreData={fetchMoreData}
          data={data}
          currentUser={currentUser}
          previousConversationId={justViewedId}
          isLoading={isLoading}
        />
      </Paper>
    </Main>
  );
};

export default List;
